import { useCallback, useMemo, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { triggerDownload } from 'utils/download';

import type { Attachment } from 'api';
import {
  getDisbursementPayoutDraftsPayoutDraftIdAttachmentsUuidDownload,
  useDeleteDisbursementPayoutDraftsPayoutDraftIdAttachmentsUuid,
  useGetDisbursementPayoutDraftsPayoutDraftIdAttachments,
  usePostDisbursementPayoutDraftsPayoutDraftIdAttachments
} from 'api';

import type { BodyType } from 'api/mutator/customAxiosInstance';

import { useOrganisationId } from 'hooks/context';

import {
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  TRANSLATIONS
} from './constants';
import { DetailsAttachmentSection } from './DetailsAttachmentSection';
import { extractUploadErrorMessage } from './utils/extractUploadErrorMessage';

type Props = {
  resourceId: number;
  className?: string;
};

export const DetailsDraftAttachmentSectionContainer = ({
  resourceId,
  className
}: Props) => {
  const notify = useOpenNotification();
  const organisationId = useOrganisationId<number>();
  const [downloadingUuid, setDownloadingUuid] = useState<string | null>(null);
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);

  // index attachments
  const { data, isLoading, isFetching, refetch } =
    useGetDisbursementPayoutDraftsPayoutDraftIdAttachments(
      organisationId,
      resourceId,
      {
        query: {
          select: (resp) => resp?.data,
          enabled: !!organisationId && !!resourceId
        }
      }
    );

  const items: Attachment[] = useMemo(() => data ?? [], [data]);

  // upload attachment
  const { mutateAsync: uploadAttachment, isPending: isUploading } =
    usePostDisbursementPayoutDraftsPayoutDraftIdAttachments({
      mutation: {
        onSuccess: () => {
          notify({
            kind: 'success',
            message: TRANSLATIONS.ATTACHMENT_UPLOAD_SUCCESS
          });
        },
        onError: (err) => {
          const specificMsg =
            extractUploadErrorMessage(err) ??
            TRANSLATIONS.ATTACHMENT_UPLOAD_ERROR;

          notify({
            kind: 'error',
            message: specificMsg
          });
        }
      }
    });

  const handleUploadFile = useCallback(
    async (file: File) => {
      // client-side size check
      if (file.size > MAX_FILE_SIZE_BYTES) {
        notify({
          kind: 'error',
          message: TRANSLATIONS.ATTACHMENT_FILE_TOO_LARGE(MAX_FILE_SIZE_MB)
        });
        return;
      }
      try {
        await uploadAttachment({
          organisationId,
          payoutDraftId: resourceId,
          data: file as BodyType<Blob>
        });
        await refetch();
      } catch {}
    },
    [uploadAttachment, organisationId, resourceId, refetch, notify]
  );

  // delete attachment
  const { mutate: deleteAttachment } =
    useDeleteDisbursementPayoutDraftsPayoutDraftIdAttachmentsUuid({
      mutation: {
        onMutate: ({ uuid }) => {
          setDeletingUuid(uuid);
        },
        onSuccess: async () => {
          await refetch();
          notify({
            kind: 'success',
            message: TRANSLATIONS.ATTACHMENT_DELETE_SUCCESS
          });
        },
        onError: () => {
          notify({
            kind: 'error',
            message: TRANSLATIONS.ATTACHMENT_DELETE_ERROR
          });
        },
        onSettled: () => {
          setDeletingUuid(null);
        }
      }
    });

  const handleDelete = useCallback(
    (uuid: string) => {
      deleteAttachment({
        organisationId,
        payoutDraftId: String(resourceId),
        uuid
      });
    },
    [deleteAttachment, organisationId, resourceId]
  );

  // download attachment
  const handleDownload = useCallback(
    async (att: Attachment) => {
      try {
        setDownloadingUuid(att.uuid);

        const resp =
          await getDisbursementPayoutDraftsPayoutDraftIdAttachmentsUuidDownload(
            organisationId,
            String(resourceId),
            att.uuid
          );

        triggerDownload(resp, att.file_name || att.uuid);
      } catch (_) {
        notify({
          kind: 'error',
          message: TRANSLATIONS.ATTACHMENT_DOWNLOAD_ERROR
        });
      } finally {
        setDownloadingUuid(null);
      }
    },
    [organisationId, resourceId, notify]
  );

  return (
    <DetailsAttachmentSection
      className={className}
      items={items}
      isLoading={isLoading}
      isFetching={isFetching}
      onDelete={handleDelete}
      onDownload={handleDownload}
      downloadingUuid={downloadingUuid}
      deletingUuid={deletingUuid}
      onUploadFile={handleUploadFile}
      isUploading={isUploading}
    />
  );
};
